import { useCallback } from 'react';
import type { DataTableMode } from '@odo/shared-ui';
import { useUrlArrayParam } from '../../../hooks/useUrlArrayParam';
import type { DmMessageInboxSort } from '../../../services/dmMessagesInbox';
import type { DmInboxStatus } from '../../../services/dmNotifications';
import { parseInboxStatus, sortForStatus } from '../shared/inboxStatus';
import { parseInboxSearch } from '../shared/listRouting';
import {
    nextSortOrder,
    usePageForSignature,
    useUpdateListParams,
    type ListParamUpdates,
    type ListSortOrder
} from '../shared/listUrlState';
import {
    CLOSED_MESSAGE_SORT_RULE,
    MESSAGE_LIST_PARAM,
    messageListParamValue,
    parseMessageMode,
    parseMessageOrder,
    parseMessageSort,
    pickMessageListSearch
} from './messageListParams';

export type MessageListFilterName = 'practiceIds' | 'assignees' | 'tags';

// Newest, most urgent, most frustrated and longest waiting (oldest first) lead on a first click.
const FIRST_CLICK_ORDER: Record<DmMessageInboxSort, ListSortOrder> = {
    latest: 'desc',
    urgency: 'desc',
    frustration: 'desc',
    waiting: 'asc'
};

// The messages list's table state, with the URL as the single source of truth.
export function useMessageListUrlState(pageSize: number) {
    const { searchParams, updateParams } = useUpdateListParams();
    const [practiceIds, setPracticeIds] = useUrlArrayParam(
        MESSAGE_LIST_PARAM.practiceIds,
        searchParams,
        updateParams
    );
    const [assignees, setAssignees] = useUrlArrayParam(
        MESSAGE_LIST_PARAM.assignees,
        searchParams,
        updateParams
    );
    const [tags, setTags] = useUrlArrayParam(MESSAGE_LIST_PARAM.tags, searchParams, updateParams);
    const search = parseInboxSearch(searchParams.get(MESSAGE_LIST_PARAM.search));
    const status = parseInboxStatus(searchParams.get(MESSAGE_LIST_PARAM.status));
    const sort = parseMessageSort(searchParams.get(MESSAGE_LIST_PARAM.sort), status);
    const order = parseMessageOrder(searchParams.get(MESSAGE_LIST_PARAM.order));
    const mode = parseMessageMode(searchParams.get(MESSAGE_LIST_PARAM.mode));
    const signature = [search, status, sort, order, mode, practiceIds, assignees, tags].join('|');
    const { page, setPage } = usePageForSignature(signature);

    const filterSetters: Record<MessageListFilterName, (value: string[]) => void> = {
        practiceIds: setPracticeIds,
        assignees: setAssignees,
        tags: setTags
    };
    const setSearch = useCallback(
        (value: string) =>
            updateParams({
                [MESSAGE_LIST_PARAM.search]: messageListParamValue('search', value)
            }),
        [updateParams]
    );
    const toggleSort = (by: DmMessageInboxSort) => {
        const nextOrder = nextSortOrder(by, { sort, order }, FIRST_CLICK_ORDER);
        updateParams({
            [MESSAGE_LIST_PARAM.sort]: messageListParamValue('sort', by),
            [MESSAGE_LIST_PARAM.order]: messageListParamValue('order', nextOrder)
        });
    };
    const setMode = (next: DataTableMode) =>
        updateParams({ [MESSAGE_LIST_PARAM.mode]: messageListParamValue('mode', next) });
    const setStatus = (next: DmInboxStatus) => {
        const nextSort = sortForStatus(sort, next, CLOSED_MESSAGE_SORT_RULE);
        const sortReset: ListParamUpdates =
            nextSort === sort
                ? {}
                : {
                      [MESSAGE_LIST_PARAM.sort]: messageListParamValue('sort', nextSort),
                      [MESSAGE_LIST_PARAM.order]: null
                  };
        updateParams({
            [MESSAGE_LIST_PARAM.status]: messageListParamValue('status', next),
            ...sortReset
        });
    };
    const clearFilters = () =>
        updateParams({
            [MESSAGE_LIST_PARAM.search]: null,
            [MESSAGE_LIST_PARAM.practiceIds]: null,
            [MESSAGE_LIST_PARAM.assignees]: null,
            [MESSAGE_LIST_PARAM.tags]: null
        });

    return {
        page,
        setPage,
        limit: pageSize,
        search,
        setSearch,
        sort,
        order,
        toggleSort,
        mode,
        setMode,
        status,
        setStatus,
        filters: { practiceIds, assignees, tags },
        setFilter: (name: MessageListFilterName, value: string[]) => filterSetters[name](value),
        clearFilters,
        listSearch: pickMessageListSearch(searchParams)
    };
}
